<template>
    <b-card :class="{ 'mx-2': showFilters, 'm-0': !showFilters }" no-body>
        <div class="m-2">
            <!-- Table Top -->
            <b-row>
                <!-- Per Page -->
                <b-col v-if="!embedded" cols="12" :md="hasSearchSlot ? 4 : 6"
                    class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
                    <label class="text-capitalize">{{ $t("table.show") }}</label>
                    <v-select :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
                        :clearable="false" class="per-page-selector d-inline-block mx-50" :value="perPage"
                        @input="$emit('per-page', $event)" />
                    <label class="text-capitalize">{{ $t("table.entries") }}</label>
                </b-col>

                <!-- Search -->
                <b-col cols="12" :md="embedded ? 12 : (hasSearchSlot ? 8 : 6)">
                    <div class="d-flex align-items-center justify-content-end table-search-actions">
                        <slot name="search">
                            <b-form-input :value="searchQuery" @input="$emit('update:searchQuery', $event)"
                                class="d-inline-block mr-1 table-search-input" :placeholder="$t('table.search')" />
                        </slot>
                        <b-button variant="primary" to="/dmp/composite_alarms/rule/"
                            v-if="$can('create', 'Dmp') && showFilters">
                            <span class="text-nowrap text-capitalize px-1">{{
                                `${$t("actions.add")} ${$tc("composite_alarm_rule.title", 1)}`
                                }}</span>
                        </b-button>
                        <button class="btn btn-success ml-1" @click="$emit('refresh-data')">
                            <feather-icon icon="RefreshCwIcon" size="14" />
                        </button>
                    </div>
                </b-col>
            </b-row>
        </div>

        <b-table ref="refTable" class="position-relative" :items="fetchList" responsive :fields="tableColumns"
            primary-key="id" :sort-by.sync="sortBy" show-empty :empty-text="$t('no_record_found')"
            :sort-desc.sync="isSortDirDesc">

            <template #head()="data">
                <span> {{ $tc(data.label, 1) }}</span>
            </template>

            <!-- Column: Actions -->
            <template #cell(actions)="data">
                <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
                    <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body"
                            data-cy="actions" />
                    </template>

                    <b-dropdown-item :to="`/dmp/composite_alarms/rule/${data.item.id}`"
                        v-if="$can('update', 'DmpCompositeAlarm')">
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50 text-capitalize">{{
                            $t("actions.edit")
                        }}</span>
                    </b-dropdown-item>

                    <b-dropdown-item @click="confirmDelete(data.item.id)" v-if="$can('delete', 'DmpCompositeAlarm')">
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50 text-capitalize">{{
                            $t("actions.delete")
                        }}</span>
                    </b-dropdown-item>
                </b-dropdown>
            </template>

        </b-table>
        <div class="mx-2 mb-2">
            <b-row>
                <b-col cols="12" sm="6"
                    class="d-flex align-items-center justify-content-center justify-content-sm-start">
                    <span class="text-muted text-capitalize">{{
                        $t("table.showing", {
                            from: dataMeta.from,
                            to: dataMeta.to,
                            of: dataMeta.of,
                        })
                    }}</span>
                </b-col>
                <!-- Pagination -->
                <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
                    <Pagination :totalList="totalList" :currentPage="currentPage"
                        @updatePagination="(val) => $emit('update', val)" :perPage="perPage">
                    </Pagination>
                </b-col>
            </b-row>
        </div>
    </b-card>
</template>

<script>
import { BBadge, BButton } from "bootstrap-vue";
import Pagination from "@/components/Pagination.vue";
import vSelect from "vue-select";
import store from "@/store";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
    props: {
        currentPage: {
            type: Number,
            require: true
        },
        dataMeta: {
            type: Object,
            require: true
        },
        fetchList: {
            type: Function,
            require: true
        },
        isSortDirDesc: {
            type: Boolean,
            require: true
        },
        perPage: {
            type: Number,
            require: true
        },
        perPageOptions: {
            type: Array,
            require: true
        },
        searchQuery: {
            type: String,
            require: true
        },
        sortBy: {
            type: String,
            require: true
        },
        tableColumns: {
            type: Array,
            require: true
        },
        totalList: {
            type: Number,
            require: true
        },
        showFilters: {
            type: Boolean,
            default: true
        },
        embedded: {
            type: Boolean,
            default: false,
        },
        showExport: {
            type: Boolean,
            default: false
        },
        exporting: {
            type: Boolean,
            default: false
        }
    },
    components: {
        BBadge,
        BButton,
        Pagination,
        vSelect
    },
    computed: {
        hasSearchSlot() {
            return Boolean(this.$scopedSlots['search'] || this.$slots['search'])
        }
    },
    setup() {


        const { t } = useI18nUtils();
        const toast = useToast();

        const trans = {
            "messages.delete.title": t("messages.delete.title"),
            "messages.delete.body": t("messages.delete.body"),
            "actions.delete": t("actions.delete"),
            "actions.cancel": t("actions.cancel"),
            "composite_alarm_rule.deleted": t("composite_alarm_rule.deleted"),
            "composite_alarm_rule.delete_error": t("composite_alarm_rule.delete_error"),
        };

        const confirmDelete = function (id) {
            const self = this;
            const storeInner = store;
            this.$swal({
                title: trans["messages.delete.title"],
                text: trans["messages.delete.body"],
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: trans["actions.delete"],
                cancelButtonText: trans["actions.cancel"],
                customClass: {
                    confirmButton: "btn btn-primary text-capitalize",
                    cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
                },
                buttonsStyling: false,
            }).then((result) => {
                if (result.value) {
                    storeInner
                        .dispatch("composite_alarm_rules/remove", id)
                        .then(() => {
                            toast({
                                component: ToastificationContent,
                                props: {
                                    title: trans["composite_alarm_rule.deleted"],
                                    icon: "CheckIcon",
                                    variant: "success",
                                },
                            });
                            self.$emit("refresh-data");
                        })
                        .catch((response) => {
                            toast(
                                {
                                    component: ToastificationContent,
                                    props: {
                                        title: response.response?.data?.message || trans["composite_alarm_rule.delete_error"],
                                        icon: "AlertTriangleIcon",
                                        variant: "danger",
                                    },
                                },
                            );
                        });
                }
            });
        };

        return { confirmDelete };
    }
}
</script>

<style scoped lang="scss">
.table-search-actions {
    flex-wrap: wrap;
    row-gap: 0.5rem;
}

.table-search-input {
    flex: 1 1 0;
    min-width: 120px;
}
</style>